/**
 * App-level event bus.
 *
 * A tiny pub-sub for cross-cutting signals the framework-agnostic
 * `api-client` needs to emit and React components need to subscribe to,
 * without coupling the two together or threading state through props.
 *
 * Today's events:
 *   - `session-expired` - token refresh failed, user must re-login
 *   - `server-error`    - backend returned 5xx
 *   - `network-error`   - fetch threw before getting a response
 *
 * Add new event types via the `AppEvent` union - TypeScript will fan out
 * the handler signatures automatically.
 */

/** Payload of a 402 response from the backend's BudgetExceeded exception. */
export interface BudgetExceededPayload {
  type: 'budget_exceeded';
  // Why the 402 fired. 'upgrade_required' = feature locked for this tier (or
  // the per-lesson version cap, on either tier); 'free_floor_exhausted' /
  // 'daily_limit_reached' = hit a per-feature daily or monthly cap;
  // 'budget_exhausted' = internal $ backstop. Absent on older backends →
  // treat as a generic limit. Copy: `lib/budget-limit.ts`.
  reason?:
    | 'upgrade_required'
    | 'budget_exhausted'
    | 'free_floor_exhausted'
    | 'daily_limit_reached';
  feature?: string | null;
  /** Binding window of a per-feature cap; absent for the backstop, tier
   *  locks and older backends. */
  window?: 'daily' | 'monthly' | null;
  message: string;
  remaining_tokens: number;
  reset_at: string | null;
  upgrade_url: string;
}

export type AppEvent =
  | { type: 'session-expired' }
  | { type: 'server-error'; status: number; endpoint: string }
  | { type: 'network-error'; endpoint: string }
  | {
      type: 'budget-exceeded';
      payload: BudgetExceededPayload;
      endpoint: string;
    };

export type AppEventType = AppEvent['type'];

type Handler<T extends AppEventType> = (
  event: Extract<AppEvent, { type: T }>
) => void;

const listeners: { [K in AppEventType]?: Set<Handler<K>> } = {};

export const appEvents = {
  /** Subscribe to an event. Returns an unsubscribe function. */
  on<T extends AppEventType>(type: T, handler: Handler<T>): () => void {
    let set = listeners[type] as Set<Handler<T>> | undefined;
    if (!set) {
      set = new Set();
      (listeners[type] as Set<Handler<T>>) = set;
    }
    set.add(handler);
    return () => set!.delete(handler);
  },

  /** Emit an event to all subscribers. Synchronous. */
  emit(event: AppEvent): void {
    const set = listeners[event.type] as
      | Set<Handler<typeof event.type>>
      | undefined;
    if (!set) return;
    for (const handler of set) {
      try {
        // `as any` is the price of the discriminated-union narrowing - each
        // handler is typed against its specific event variant at the call
        // site, the runtime cast here is safe by construction.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (handler as any)(event);
      } catch (err) {
        // A failing subscriber must not block others.
        console.error('app-events subscriber threw:', err);
      }
    }
  },
};
